/**
 * ThinkingPlanCard — Structured plan card with collapsible steps and log viewer.
 *
 * Replaces StepsGuideModule for playground chat. Renders a plan card per agent round
 * with status-aware step items, fold/unfold per step, and a monospace log viewer
 * for active/expanded steps.
 *
 * Data slots (from useNanoClawResults thinking card):
 *   - title: string — current activity context
 *   - steps: array — step objects with { label, content, status, humanizedEmoji, humanizedText, ts }
 *   - status: string — "active" | "done" | "error" | "cancelled"
 *   - conclusion: string — optional final text
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ── Status configuration ──
const STATUS_CONFIG = {
  thinking: { icon: '💭', color: 'rgba(88,86,214,0.7)', label: 'thinking' },
  tool_use: { icon: '🔧', color: 'rgba(255,149,0,0.8)', label: 'tool_use' },
  text: { icon: '✏️', color: 'rgba(52,199,89,0.7)', label: 'text' },
  speech: { icon: '🗣️', color: 'rgba(59,130,246,0.8)', label: 'speech' },
  status: { icon: '📋', color: 'rgba(142,142,147,0.5)', label: 'status' },
  done: { icon: '✅', color: 'rgba(52,199,89,0.8)', label: 'done' },
  error: { icon: '❌', color: 'rgba(255,59,48,0.8)', label: 'error' },
  cancelled: { icon: '⏹', color: 'rgba(142,142,147,0.6)', label: 'cancelled' },
};

const PLAN_STATUS_CONFIG = {
  active: { bg: 'rgba(88,86,214,0.08)', border: 'rgba(88,86,214,0.15)', dot: '#5856d6' },
  done: { bg: 'rgba(52,199,89,0.06)', border: 'rgba(52,199,89,0.12)', dot: '#34c759' },
  error: { bg: 'rgba(255,59,48,0.06)', border: 'rgba(255,59,48,0.12)', dot: '#ff3b30' },
  cancelled: { bg: 'rgba(142,142,147,0.06)', border: 'rgba(142,142,147,0.12)', dot: '#8e8e93' },
};

/** Shorten raw tool labels to be user-friendly */
function shortenLabel(label) {
  if (!label) return 'Working...';
  if (label.startsWith('Read:') || label.startsWith('Read ')) return 'Reading files';
  if (label.startsWith('Edit:') || label.startsWith('Edit ')) return 'Editing';
  if (label.startsWith('Write:') || label.startsWith('Write ')) return 'Writing';
  if (label.startsWith('Bash:') || label.startsWith('Bash ')) return 'Running command';
  if (label.startsWith('Grep:') || label.startsWith('Grep ')) return 'Searching';
  if (label.startsWith('Glob:') || label.startsWith('Glob ')) return 'Finding files';
  if (label.startsWith('WebFetch') || label.startsWith('WebSearch')) return 'Searching web';
  const cleaned = label.replace(/\/[\w/.:-]+/g, '').trim();
  const words = cleaned.split(/\s+/).slice(0, 5).join(' ');
  return words || 'Working...';
}

/** Normalize a step to object form — handles plain strings from agent-published cards */
function normalizeStep(step) {
  if (typeof step === 'string') {
    return { label: step, content: '', status: 'done' };
  }
  return step;
}

/** Parse steps from various formats (same as StepsGuideModule) */
function parseSteps(raw) {
  let steps;
  if (Array.isArray(raw)) {
    steps = raw;
  } else if (typeof raw !== 'string' || !raw.trim()) {
    return [];
  } else {
    try {
      const parsed = JSON.parse(raw);
      steps = Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      try {
        const parts = raw.replace(/\}\s*\{/g, '}|||{').split('|||');
        steps = parts.map(p => JSON.parse(p)).filter(Boolean);
      } catch {
        return [];
      }
    }
  }
  return steps.map(normalizeStep);
}

// ── StepLogViewer — monospace log content for a single step ──
function StepLogViewer({ content }) {
  const logRef = useRef(null);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [content]);

  if (!content) return null;

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      style={{ overflow: 'hidden' }}
    >
      <div
        ref={logRef}
        style={{
          marginTop: 6,
          padding: '8px 10px',
          borderRadius: 6,
          background: 'rgba(0,0,0,0.03)',
          fontSize: 11,
          fontFamily: "'SF Mono', 'Cascadia Code', 'JetBrains Mono', monospace",
          color: 'rgba(0,0,0,0.45)',
          lineHeight: 1.6,
          maxHeight: 200,
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {content}
      </div>
    </motion.div>
  );
}

// ── PlanStepItem — single step with status, label, collapsible log ──
/** Format epoch-ms timestamp to HH:MM:SS AM/PM */
function formatStepTime(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m}:${s} ${ampm}`;
}

function PlanStepItem({ step, isActive, isLast, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Auto-expand active step, auto-collapse when done
  useEffect(() => {
    if (isActive) setExpanded(true);
  }, [isActive]);

  const statusCfg = STATUS_CONFIG[step.status] || STATUS_CONFIG.thinking;
  // Backend humanizeToolUse() produces "emoji Action" labels — split emoji from text
  const labelParts = (step.label || '').match(/^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F?)\s*(.*)/u);
  const emoji = step.humanizedEmoji || (labelParts ? labelParts[1] : null) || statusCfg.icon || '•';
  const labelText = labelParts ? labelParts[2] : step.label;
  const displayText = step.humanizedText || labelText || shortenLabel(step.content) || 'Working...';
  const hasContent = step.content && step.content.length > 0;
  const timeStr = formatStepTime(step.ts);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
    >
      <div
        onClick={() => hasContent && setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: '5px 8px', borderRadius: 6,
          cursor: hasContent ? 'pointer' : 'default',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { if (hasContent) e.currentTarget.style.background = 'rgba(0,0,0,0.03)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Status indicator */}
        <span style={{
          fontSize: 13, lineHeight: '20px', flexShrink: 0,
          opacity: isActive ? 1 : 0.6,
        }}>
          {emoji}
        </span>

        {/* Step text + badge + timestamp */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {/* Status badge */}
            <span style={{
              fontSize: 9,
              fontFamily: "'SF Mono', 'Cascadia Code', monospace",
              padding: '1px 5px',
              borderRadius: 3,
              background: statusCfg.color.replace(/[\d.]+\)$/, '0.12)'),
              color: statusCfg.color,
              fontWeight: 600,
              letterSpacing: '0.02em',
              lineHeight: '16px',
              flexShrink: 0,
            }}>
              {statusCfg.label}
            </span>

            {/* Timestamp */}
            {timeStr && (
              <span style={{
                fontSize: 9,
                fontFamily: "'SF Mono', 'Cascadia Code', monospace",
                color: 'rgba(0,0,0,0.25)',
                lineHeight: '16px',
                flexShrink: 0,
              }}>
                {timeStr}
              </span>
            )}
          </div>

          {/* Step label */}
          <span style={{
            fontSize: 12.5,
            lineHeight: '20px',
            color: isActive ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.4)',
            fontWeight: isActive ? 500 : 400,
          }}>
            {displayText}
          </span>

          {/* Expandable log viewer */}
          <AnimatePresence>
            {expanded && hasContent && (
              <StepLogViewer content={step.content} />
            )}
          </AnimatePresence>
        </div>

        {/* Expand indicator */}
        {hasContent && (
          <span style={{
            fontSize: 10, color: 'rgba(0,0,0,0.2)', lineHeight: '20px',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            flexShrink: 0,
          }}>
            ▼
          </span>
        )}
      </div>
    </motion.div>
  );
}

// ── ThinkingPlanCard — main component ──
export default function ThinkingPlanCard({ data, cardStatus }) {
  const [collapsed, setCollapsed] = useState(false);

  if (!data) return null;

  const steps = parseSteps(data.steps);
  const planStatus = data.status || (cardStatus === 'finalized' ? 'done' : 'active');
  const planCfg = PLAN_STATUS_CONFIG[planStatus] || PLAN_STATUS_CONFIG.active;
  const isActive = planStatus === 'active';
  const isDone = planStatus === 'done';
  const title = data.title || (isDone ? 'Complete' : 'Thinking...');

  // Find last active step index
  const activeIdx = steps.length - 1;

  return (
    <div style={{
      borderRadius: 10,
      border: `1px solid ${planCfg.border}`,
      background: planCfg.bg,
      overflow: 'hidden',
      transition: 'all 0.3s ease',
    }}>
      {/* Header */}
      <div
        onClick={() => setCollapsed(c => !c)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {/* Status dot / spinner */}
        {isActive ? (
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            border: `2px solid ${planCfg.dot}`,
            borderTopColor: 'transparent',
            animation: 'thinkingSpin 0.8s linear infinite',
            flexShrink: 0,
          }} />
        ) : (
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: planCfg.dot, flexShrink: 0,
          }} />
        )}

        {/* Title */}
        <span style={{
          fontSize: 12, fontWeight: 600,
          color: 'rgba(0,0,0,0.6)',
          flex: 1,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {title}
        </span>

        {/* Step count */}
        <span style={{
          fontSize: 10, color: 'rgba(0,0,0,0.3)',
          fontFamily: "'SF Mono', monospace",
        }}>
          {steps.length} step{steps.length !== 1 ? 's' : ''}
        </span>

        {/* Collapse indicator */}
        <span style={{
          fontSize: 10, color: 'rgba(0,0,0,0.25)',
          transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }}>
          ▼
        </span>
      </div>

      {/* Steps list */}
      <AnimatePresence>
        {!collapsed && steps.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={{
              padding: '2px 6px 8px',
              borderTop: `1px solid ${planCfg.border}`,
            }}>
              {steps.map((step, i) => (
                <PlanStepItem
                  key={`${i}-${step.ts || ''}`}
                  step={step}
                  isActive={isActive && i === activeIdx}
                  isLast={i === steps.length - 1}
                  defaultExpanded={isActive && i === activeIdx}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inline CSS for spinner animation */}
      <style>{`
        @keyframes thinkingSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
